/**
 * Account UI (paywall plan P1, redesign 2026-11, docs/DESIGN.md artboards 8–10): the sign-in screen (email + password,
 * an emailed code, a new account with a password, forgot password, the new account's name and colour), the account
 * button and its menu (name + colour, password, Plan & billing, sign out, delete account) and the password dialog.
 * Accounts are optional — they're only needed to plan together (lib/auth.ts).
 *
 * The sign-in screen is a native modal <dialog> over the whole window: the left pane on the shell, the right side a
 * framed window onto the live app behind it (a transparent frame whose huge box-shadow paints the rest of the window).
 */
import {
  ArrowRight, Check, CircleAlert, Eye, EyeOff, KeyRound, LoaderCircle, Lock, LogOut, Mail, MailCheck, ShieldCheck, Trash2, Users, WifiOff, X,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  accountsEnabled, AuthProblem, authProblem, checkEmail, checkNewPassword, closeSignIn, CODE_LENGTH, CODE_MAX, deleteAccount, finishSignUp,
  openAccountMenu, openSignIn, passwordRules, resendSignUpCode, sendEmailCode, sendPasswordReset, sendReauthCode, setPassword, signInWithPassword, signOut,
  startSignUp, updateProfile, useAccount, verifyEmailCode, verifyRecoveryCode, type AuthField,
} from "../lib/auth.ts";
import { DEFAULT_SERVER, useApp } from "../state/store.ts";
import { AcademyLink } from "./Academy.tsx";
import { LegalLink } from "./Legal.tsx";
import { BillingSection, useModal } from "./Plans.tsx";
import "./account.css";

/** the 12 colours people pick for their cursor, name tag and plans in shared worlds (mockup "Register") */
export const PROFILE_COLORS: { c: string; n: string }[] = [
  { c: "#6fbf4a", n: "Green" }, { c: "#60b2e8", n: "Sky" }, { c: "#f06484", n: "Rose" }, { c: "#e8b04a", n: "Amber" },
  { c: "#a888f6", n: "Violet" }, { c: "#40c8ac", n: "Teal" }, { c: "#f0884c", n: "Orange" }, { c: "#828ef8", n: "Indigo" },
  { c: "#b2d654", n: "Lime" }, { c: "#d97ae6", n: "Fuchsia" }, { c: "#8fa3b8", n: "Slate" }, { c: "#e85a4a", n: "Red" },
];
const RESEND_SECONDS = 60;
const LAST_EMAIL = "mcp-last-email";

/** MC Planner's mark (the logo's block and amber dot) */
export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.2 20.4 8 12 12.8 3.6 8Z" fill="#8fd96a" /><path d="M3.6 8 12 12.8v8L3.6 16Z" fill="#6fbf4a" />
      <path d="M20.4 8 12 12.8v8l8.4-4.8Z" fill="#4c8f30" /><circle cx="18.6" cy="4.4" r="2.1" fill="#e8b04a" />
    </svg>
  );
}

const Spin = () => <LoaderCircle size={16} className="spin" aria-hidden="true" />;

function useOnline() {
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine !== false);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

// ---------------- fields ----------------

type Errors = Partial<Record<AuthField, string>>;

function FieldError({ id, text }: { id: string; text?: string }) {
  return text ? <p className="field-error" id={id} role="alert"><CircleAlert size={13} aria-hidden="true" />{text}</p> : null;
}

function EmailField({ value, onChange, error, autoFocus }: { value: string; onChange: (v: string) => void; error?: string; autoFocus?: boolean }) {
  return (
    <div className="auth-field">
      <label htmlFor="auth-email" className="field-label">Email</label>
      <label className={`field-box auth-box${error ? " invalid" : ""}`}>
        <Mail size={16} aria-hidden="true" />
        <input id="auth-email" name="email" type="email" autoComplete="username" inputMode="email" spellCheck={false} required enterKeyHint="next"
          value={value} onChange={(e) => onChange(e.target.value)} placeholder="you@example.com" autoFocus={autoFocus} data-autofocus={autoFocus ? "" : undefined}
          aria-invalid={!!error || undefined} aria-describedby={error ? "auth-email-err" : undefined} />
      </label>
      <FieldError id="auth-email-err" text={error} />
    </div>
  );
}

function PasswordField({ id, label, value, onChange, error, autoComplete, extra, autoFocus, hint }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string; autoComplete: "current-password" | "new-password";
  extra?: ReactNode; autoFocus?: boolean; hint?: string;
}) {
  const [show, setShow] = useState(false);
  const described = [error ? `${id}-err` : "", hint ? `${id}-hint` : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className="auth-field">
      <div className="auth-label-row"><label htmlFor={id} className="field-label">{label}</label>{extra}</div>
      <div className={`field-box auth-box with-action${error ? " invalid" : ""}`}>
        <Lock size={16} aria-hidden="true" />
        <input id={id} name={id} type={show ? "text" : "password"} autoComplete={autoComplete} required value={value} autoFocus={autoFocus} data-autofocus={autoFocus ? "" : undefined}
          onChange={(e) => onChange(e.target.value)} aria-invalid={!!error || undefined} aria-describedby={described} spellCheck={false} />
        <button type="button" className="btn btn-sm btn-ghost btn-icon auth-eye" onClick={() => setShow(!show)} aria-pressed={show}
          aria-label={show ? "Hide password" : "Show password"} title={show ? "Hide password" : "Show password"}>
          {show ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </button>
      </div>
      {hint && !error && <p className="field-hint" id={`${id}-hint`}>{hint}</p>}
      <FieldError id={`${id}-err`} text={error} />
    </div>
  );
}

/** the new password's rules, ticked off while typing */
function Rules({ password, again }: { password: string; again: string }) {
  return (
    <ul className="auth-rules" aria-label="Password rules">
      {passwordRules(password, again).map((r) => (
        <li key={r.id} className={r.ok ? "ok" : ""}><span className="rule-tick" aria-hidden="true">{r.ok && <Check size={11} strokeWidth={3} />}</span>{r.label}<span className="sr-only">{r.ok ? " (done)" : ""}</span></li>
      ))}
    </ul>
  );
}

/**
 * The emailed code: one real input (paste, autofill and backspace work as usual) drawn as boxes. Starts with
 * CODE_LENGTH boxes and grows when a longer code comes in.
 */
function CodeBoxes({ value, onChange, error, onFilled, label = "Code" }: { value: string; onChange: (v: string) => void; error?: string; onFilled?: (v: string) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const n = Math.max(CODE_LENGTH, Math.min(CODE_MAX, value.length));
  const sep = Math.ceil(n / 2) - 1;
  const active = Math.min(value.length, n - 1);
  useEffect(() => { input.current?.focus(); }, []);
  const atEnd = () => { const el = input.current; if (el) requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length)); };
  return (
    <fieldset className="code-field">
      <legend className="field-label">{label}</legend>
      <div className={`code-boxes${error ? " invalid" : ""}`} onPointerDown={(e) => { e.preventDefault(); input.current?.focus(); atEnd(); }}>
        <input
          ref={input} className="code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={CODE_MAX + 4}
          value={value} aria-label={`${label}, ${CODE_LENGTH} digits from the email`} aria-invalid={!!error || undefined} aria-describedby={error ? "auth-code-err" : undefined}
          onFocus={() => { setFocused(true); atEnd(); }} onBlur={() => setFocused(false)}
          onChange={(e) => {
            const next = e.target.value.replace(/\D/g, "").slice(0, CODE_MAX);
            onChange(next);
            // a whole code at once (pasted, or filled in from the email by the system): go on by itself. Typing digit by
            // digit doesn't (a project set to longer codes would be sent too early); Enter or the button sends it.
            const typed = next.length === value.length + 1 && next.startsWith(value);
            if (next.length >= CODE_LENGTH && next !== value && !typed) onFilled?.(next);
          }}
        />
        {Array.from({ length: n }, (_, i) => (
          <span key={i} className="code-slot-wrap">
            <span className={`code-slot${value[i] ? " filled" : ""}${focused && i === active ? " active" : ""}`} aria-hidden="true">
              {value[i] ?? ""}{focused && i === active && !value[i] && <i className="code-caret" />}
            </span>
            {i === sep && n > 4 && <span className="code-sep" aria-hidden="true" />}
          </span>
        ))}
      </div>
      <FieldError id="auth-code-err" text={error} />
    </fieldset>
  );
}

// ---------------- the sign-in screen ----------------

export function SignInDialog() {
  const dialog = useAccount((s) => s.dialog);
  if (!dialog.open) return null;
  if (!accountsEnabled()) return <AccountsOff />;
  return <AuthScreen reason={dialog.reason} start={dialog.step} />;
}

type Step = "signin" | "code" | "signup" | "forgot" | "new-password" | "profile";
/** what the code is for: a sign-in code, a new account's confirmation (or a sign-in code for an address that had one), a reset */
type Purpose = "signin" | "signup" | "signup-email" | "recovery";

/** why the screen opened, from the gate's reason (lib/auth.ts openSignIn) */
function contextOf(reason: string | undefined) {
  const s = useApp.getState();
  if (reason && /join/i.test(reason)) {
    const p = s.accountInvite?.preview;
    return { kind: "join" as const, world: p?.world_name ?? undefined, owner: p?.owner_name ?? undefined };
  }
  if (reason && /plan(ning)? together/i.test(reason)) return { kind: "together" as const, world: s.room?.name ?? s.level?.name ?? undefined };
  return { kind: "other" as const };
}

/** "Send again in 0:42" */
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function AuthScreen({ reason, start }: { reason?: string; start?: "new-password" }) {
  const ref = useModal(() => closeSignIn());
  const user = useAccount((s) => s.user);
  const profile = useAccount((s) => s.profile);
  const online = useOnline();
  const [ctx] = useState(() => contextOf(reason));
  const [step, setStep] = useState<Step>(start ?? "signin");
  const [purpose, setPurpose] = useState<Purpose>("signin");
  const [back, setBack] = useState<Step>("signin");
  const [email, setEmail] = useState(() => { try { return localStorage.getItem(LAST_EMAIL) ?? ""; } catch { return ""; } });
  const [password, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [resendIn, setResendIn] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  /** after the new password: carry on (close), or the profile of a new account */
  const [afterPassword, setAfterPassword] = useState<"close" | "profile">("close");
  const titleId = useId();

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const go = (s: Step) => { setStep(s); setErrors({}); setNote(null); };
  const run = async (what: string, f: () => Promise<void>) => {
    if (busy) return;
    setBusy(what);
    setErrors({});
    try { await f(); } catch (e) { const p = authProblem(e); setErrors({ [p.field]: p.message }); } finally { setBusy(null); }
  };
  const remember = () => { try { localStorage.setItem(LAST_EMAIL, email.trim()); } catch { /* ignore */ } };
  const needEmail = () => { const p = checkEmail(email); if (p) throw p; };

  const done = (isNew: boolean) => {
    if (isNew) go("profile");
    else closeSignIn();
  };

  // ---- actions ----
  const doSignIn = () => run("signin", async () => {
    needEmail();
    if (!password) throw new AuthProblem("Enter your password.", "password");
    await signInWithPassword(email, password);
    remember();
  });
  const sendCode = (p: Purpose, resend = false) => run(resend ? "resend" : "send", async () => {
    needEmail();
    if (p === "recovery") await sendPasswordReset(email);
    else if (p === "signup") await resendSignUpCode(email);
    else if (p === "signup-email") await sendEmailCode(email, { existingOnly: true });
    else await sendEmailCode(email);
    remember();
    setPurpose(p);
    if (step !== "code") { setBack(step); setStep("code"); }
    setCode("");
    setResendIn(RESEND_SECONDS);
    setNote(resend ? "We sent you a new code." : null);
  });
  const doSignUp = () => run("signup", async () => {
    needEmail();
    const pw = checkNewPassword(password, again);
    if (pw) throw pw;
    const r = await startSignUp(email, password);
    remember();
    if (r.via === "done") { done(true); return; }
    setPurpose(r.via === "email" ? "signup-email" : "signup");
    setBack("signup");
    setStep("code");
    setCode("");
    setResendIn(RESEND_SECONDS);
  });
  const verify = (value = code) => run("verify", async () => {
    if (value.replace(/\s+/g, "").length < CODE_LENGTH) throw new AuthProblem(`Enter the ${CODE_LENGTH}-digit code from the email.`, "code");
    if (purpose === "recovery") {
      await verifyRecoveryCode(email, value);
      setPw(""); setAgain(""); setAfterPassword("close");
      go("new-password");
      return;
    }
    if (purpose === "signin") {
      const { isNew } = await verifyEmailCode(email, value, { hold: true });
      done(isNew);
      return;
    }
    const r = await finishSignUp(email, value, password, purpose === "signup" ? "signup" : "email");
    if (r.passwordProblem) {
      // signed in, but the password didn't stick: ask for it again
      setAfterPassword(r.isNew ? "profile" : "close");
      setPw(""); setAgain("");
      setStep("new-password");
      setErrors({ password: r.passwordProblem.message });
      return;
    }
    done(r.isNew);
  });
  const savePassword = () => run("password", async () => {
    const pw = checkNewPassword(password, again);
    if (pw) throw pw;
    const r = await setPassword(password);
    if (!r.saved) {
      // (a fresh sign-in never needs more; if the project asks anyway, the account menu's password dialog can do it)
      throw new AuthProblem(r.error ?? "Supabase asks for one more step. Set the password from the account menu instead.", "password");
    }
    if (afterPassword === "profile") go("profile");
    else {
      closeSignIn();
      useApp.getState().set({ notice: { text: "Password saved. Next time you can sign in with it." } });
    }
  });

  // ---- profile (new account) ----
  const [name, setName] = useState("");
  const [color, setColor] = useState(PROFILE_COLORS[0].c);
  const [profileInit, setProfileInit] = useState(false);
  useEffect(() => {
    if (step !== "profile" || profileInit || !profile) return;
    setProfileInit(true);
    setName(profile.displayName);
    setColor(PROFILE_COLORS.some((x) => x.c === profile.color) ? profile.color : PROFILE_COLORS[0].c);
  }, [step, profile, profileInit]);
  const saveProfile = () => run("profile", async () => {
    if (!name.trim()) throw new AuthProblem("Enter a name: friends see it next to your cursor.", "name");
    await updateProfile({ displayName: name, color });
    closeSignIn();
  });

  // ---- what each step shows ----
  let chip: ReactNode = null;
  let title = "Sign in to MC Planner";
  let lead: ReactNode = "An account is only needed to plan together with friends. Planning on your own never needs one.";
  if (step === "signin") {
    if (ctx.kind === "together") {
      title = "Sign in to plan together";
      lead = "A free account lets everyone see who's who. Planning alone never needs one.";
      if (ctx.world) chip = <span className="auth-chip"><Users size={14} aria-hidden="true" /><span>To plan together in <b>{ctx.world}</b></span></span>;
    } else if (ctx.kind === "join") {
      title = "Sign in to join";
      lead = "Joining is free: you only need a free account. Planning alone never needs one.";
      if (ctx.world) chip = <span className="auth-chip"><Users size={14} aria-hidden="true" /><span>{ctx.owner ? <>{ctx.owner} invited you to <b>{ctx.world}</b></> : <>To join <b>{ctx.world}</b></>}</span></span>;
    } else if (reason) {
      lead = <>{reason} Planning on your own never needs an account.</>;
    }
  } else if (step === "signup") {
    title = "Create your free account";
    lead = "One Lab43 account for MC Planner and the Minecraft mod. Planning alone never needs one.";
  } else if (step === "forgot") {
    title = "Reset your password";
    lead = "We'll email you a code. Then you choose a new password.";
  } else if (step === "code") {
    chip = <span className="auth-chip"><MailCheck size={14} aria-hidden="true" className="accent" />Code sent</span>;
    title = "Check your email";
    lead = <>We sent {CODE_LENGTH === 8 ? "an" : "a"} {CODE_LENGTH}-digit code to <span className="auth-strong">{email.trim()}</span>{purpose === "recovery" ? " to reset your password" : ""}. It can take a minute; check spam too.</>;
  } else if (step === "new-password") {
    title = afterPassword === "profile" || purpose.startsWith("signup") ? "Set your password" : "Choose a new password";
    lead = "Then you can sign in with your email and this password, or keep using emailed codes.";
  } else if (step === "profile") {
    chip = <span className="auth-chip ok"><Check size={14} strokeWidth={2.5} aria-hidden="true" />Free account created</span>;
    title = "How should friends see you?";
    lead = "Your name and colour mark your cursor and the plans you make in shared worlds. Change them any time.";
  }

  const offlineNote = !online && <p className="auth-offline" role="status"><WifiOff size={14} aria-hidden="true" />You're offline. Signing in needs the internet.</p>;
  const formError = errors.form && <p className="field-error auth-form-error" role="alert"><CircleAlert size={13} aria-hidden="true" />{errors.form}</p>;

  return (
    <dialog ref={ref} className="auth-screen modal signin mcp-acct" aria-labelledby={titleId}>
      <section className="auth-pane">
        <header className="auth-head">
          <span className="brand"><Logo />MC Planner</span>
          {step === "profile"
            ? <span className="caption">Almost there</span>
            : <button type="button" className="btn btn-sm btn-ghost auth-close" onClick={closeSignIn} title="Not now (Esc)"><X size={16} aria-hidden="true" />Not now</button>}
        </header>

        <div className="auth-body" key={step}>
          {chip}
          <h1 className="title-lg auth-title" id={titleId}>{title}</h1>
          <p className="auth-lead">{lead}</p>
          {offlineNote}

          {step === "signin" && (
            <form className="auth-form signin-form" onSubmit={(e) => { e.preventDefault(); void doSignIn(); }} noValidate>
              <EmailField value={email} onChange={setEmail} error={errors.email} autoFocus={!email} />
              <PasswordField id="auth-password" label="Password" value={password} onChange={setPw} error={errors.password} autoComplete="current-password" autoFocus={!!email}
                extra={<button type="button" className="auth-link" onClick={() => go("forgot")}>Forgot password?</button>} />
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "signin" ? <><Spin />Signing in…</> : <>Sign in<ArrowRight size={16} aria-hidden="true" /></>}
              </button>
              {formError}
              <div role="separator" className="auth-or"><span />or<span /></div>
              <button type="button" className="btn btn-lg btn-outline btn-block auth-code-btn" disabled={!!busy} onClick={() => void sendCode("signin")}>
                {busy === "send" ? <><Spin />Sending…</> : <><Mail size={16} aria-hidden="true" className="muted-ico" />Email me a sign-in code</>}
              </button>
              <p className="auth-help">No password yet? The code signs you in, and creates your free account if you're new.</p>
              <p className="auth-help">New here and want a password? <button type="button" className="auth-link strong" onClick={() => { setAgain(""); go("signup"); }}>Create an account</button></p>
            </form>
          )}

          {step === "signup" && (
            <form className="auth-form signup-form" onSubmit={(e) => { e.preventDefault(); void doSignUp(); }} noValidate>
              <EmailField value={email} onChange={setEmail} error={errors.email} autoFocus={!email} />
              <PasswordField id="auth-new-password" label="Password" value={password} onChange={setPw} error={errors.password} autoComplete="new-password" autoFocus={!!email} />
              <PasswordField id="auth-again" label="Repeat password" value={again} onChange={setAgain} error={errors.again} autoComplete="new-password" />
              <Rules password={password} again={again} />
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "signup" ? <><Spin />Creating your account…</> : <>Create account<ArrowRight size={16} aria-hidden="true" /></>}
              </button>
              {formError}
              <p className="auth-help">We email you a code to confirm the address. Already have an account? <button type="button" className="auth-link strong" onClick={() => go("signin")}>Sign in</button></p>
            </form>
          )}

          {step === "forgot" && (
            <form className="auth-form forgot-form" onSubmit={(e) => { e.preventDefault(); void sendCode("recovery"); }} noValidate>
              <EmailField value={email} onChange={setEmail} error={errors.email} autoFocus />
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "send" ? <><Spin />Sending…</> : "Email me a code"}
              </button>
              {formError}
              <p className="auth-help"><button type="button" className="auth-link strong" onClick={() => go("signin")}>Back to sign in</button></p>
            </form>
          )}

          {step === "code" && (
            <form className="auth-form code-form" onSubmit={(e) => { e.preventDefault(); void verify(); }} noValidate>
              <CodeBoxes value={code} onChange={(v) => { setCode(v); if (errors.code) setErrors({}); }} error={errors.code} onFilled={(v) => void verify(v)} />
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "verify" ? <><Spin />Checking…</> : "Verify and continue"}
              </button>
              {formError}
              {note && <p className="auth-note" role="status">{note}</p>}
              <div className="auth-code-row">
                {resendIn > 0
                  ? <span className="tabular auth-wait">Send again in {mmss(resendIn)}</span>
                  : <button type="button" className="auth-link" disabled={!!busy} onClick={() => void sendCode(purpose, true)}>{busy === "resend" ? "Sending…" : "Send a new code"}</button>}
                <button type="button" className="auth-link" onClick={() => go(back)}>Use a different email</button>
              </div>
            </form>
          )}

          {step === "new-password" && (
            <form className="auth-form password-form" onSubmit={(e) => { e.preventDefault(); void savePassword(); }} noValidate>
              <input type="email" name="email" autoComplete="username" value={user?.email ?? email} readOnly hidden />
              <PasswordField id="auth-new-password" label="New password" value={password} onChange={setPw} error={errors.password} autoComplete="new-password" autoFocus />
              <PasswordField id="auth-again" label="Repeat password" value={again} onChange={setAgain} error={errors.again} autoComplete="new-password" />
              <Rules password={password} again={again} />
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "password" ? <><Spin />Saving…</> : "Save password"}
              </button>
              {formError}
              <p className="auth-help"><button type="button" className="auth-link" onClick={() => (afterPassword === "profile" ? go("profile") : closeSignIn())}>Skip for now</button> · you're signed in either way.</p>
            </form>
          )}

          {step === "profile" && (
            <form className="auth-form profile-form" onSubmit={(e) => { e.preventDefault(); void saveProfile(); }} noValidate>
              <div className="auth-field">
                <label htmlFor="auth-name" className="field-label">Name</label>
                <label className={`field-box auth-box${errors.name ? " invalid" : ""}`}>
                  <input id="auth-name" name="nickname" autoComplete="nickname" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoFocus
                    aria-invalid={!!errors.name || undefined} aria-describedby={errors.name ? "auth-name-err" : "auth-name-count"} />
                  <span className="caption tabular" id="auth-name-count">{[...name].length} / 40</span>
                </label>
                <FieldError id="auth-name-err" text={errors.name} />
              </div>
              <Swatches value={color} onChange={setColor} />
              <div className="auth-preview" aria-label="Preview">
                <span className="preview-cursor" style={{ "--c": color } as CSSProperties}>
                  <Cursor color={color} />
                  <span className="name-tag">{name.trim() || "You"}</span>
                </span>
                <span className="preview-rule" aria-hidden="true" />
                <span className="preview-obj"><b>Main road</b><span className="caption"><i className="dot" style={{ background: color }} />Added by {name.trim() || "you"}</span></span>
              </div>
              <button type="submit" className="btn btn-lg btn-primary btn-block auth-submit" disabled={!!busy}>
                {busy === "profile" ? <><Spin />Saving…</> : <>Continue<ArrowRight size={16} aria-hidden="true" /></>}
              </button>
              {formError}
            </form>
          )}
        </div>

        {step === "profile"
          ? (
            <footer className="auth-foot row-foot">
              <span>Signed in as <span className="auth-strong">{user?.email ?? email}</span></span>
              <button type="button" className="auth-link" onClick={() => void run("signout", async () => { await signOut(); go("signin"); })}>Not you?</button>
            </footer>
          )
          : (
            <footer className="auth-foot">
              <span className="auth-lab43"><ShieldCheck size={14} aria-hidden="true" />One Lab43 account, also for the Minecraft mod<span className="mono">auth.lab43.dev</span></span>
              <span className="auth-legal">
                By continuing you agree to the <LegalLink path="legal/terms.html">Terms</LegalLink> and the <LegalLink path="legal/privacy.html">Privacy policy</LegalLink>.{" "}
                <AcademyLink to="appSignIn" about="signing in">Sign-in help</AcademyLink>
              </span>
            </footer>
          )}
      </section>

      <div className="auth-stage" aria-hidden="true">
        <div className="auth-window" />
        <div className="auth-caption">
          <span className="title-md">Plan together, live.</span>
          <span>Everyone sees the same plan as it changes. Friends join free, with their own blocks.</span>
        </div>
      </div>
    </dialog>
  );
}

/** a build without accounts (VITE_SUPABASE_PUBLISHABLE_KEY=""): say so instead of a sign-in form */
function AccountsOff() {
  const ref = useModal(() => closeSignIn());
  return (
    <dialog ref={ref} className="auth-screen modal signin mcp-acct" aria-labelledby="auth-off-title">
      <section className="auth-pane">
        <header className="auth-head"><span className="brand"><Logo />MC Planner</span></header>
        <div className="auth-body">
          <h1 className="title-lg auth-title" id="auth-off-title">Accounts are off in this version</h1>
          <p className="auth-lead">This copy of MC Planner was built without accounts, so planning together isn't available here. Planning alone works as always.</p>
          <div className="auth-form"><button type="button" className="btn btn-lg btn-primary btn-block" autoFocus data-autofocus="" onClick={closeSignIn}>Back to planning</button></div>
        </div>
      </section>
      <div className="auth-stage" aria-hidden="true"><div className="auth-window" /></div>
    </dialog>
  );
}

function Cursor({ color }: { color: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="preview-arrow">
      <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" fill={color} stroke="#0f1416" strokeWidth="1.5" />
    </svg>
  );
}

function Swatches({ value, onChange, small = false }: { value: string; onChange: (c: string) => void; small?: boolean }) {
  const id = useId();
  return (
    <div className={`auth-field${small ? " small" : ""}`}>
      <span className="field-label" id={id}>Colour</span>
      <div className={`acct-swatches${small ? " small" : ""}`} role="radiogroup" aria-labelledby={id}>
        {PROFILE_COLORS.map(({ c, n }) => (
          <button key={c} type="button" role="radio" aria-checked={c === value} aria-label={n} title={n} className={`swatch${c === value ? " on" : ""}`}
            style={{ "--c": c } as CSSProperties} onClick={() => onChange(c)}
            onKeyDown={(e) => {
              const i = PROFILE_COLORS.findIndex((x) => x.c === value);
              const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
              if (!step) return;
              e.preventDefault();
              const next = PROFILE_COLORS[(Math.max(0, i) + step + PROFILE_COLORS.length) % PROFILE_COLORS.length].c;
              onChange(next);
              (e.currentTarget.parentElement?.querySelector(`[aria-label="${PROFILE_COLORS.find((x) => x.c === next)?.n}"]`) as HTMLElement | null)?.focus();
            }}
            tabIndex={c === value || (!PROFILE_COLORS.some((x) => x.c === value) && c === PROFILE_COLORS[0].c) ? 0 : -1}>
            {c === value && <Check size={small ? 12 : 16} strokeWidth={3} aria-hidden="true" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------- account button + menu ----------------

/** Menubar / setup screen: "Sign in", or the avatar with the account menu. */
export function AccountButton({ compact = false }: { compact?: boolean }) {
  const status = useAccount((s) => s.status);
  const profile = useAccount((s) => s.profile);
  const open = useAccount((s) => s.menu);
  const wrap = useRef<HTMLDivElement>(null);
  if (!accountsEnabled()) return null;
  if (status === "checking") return <span className="account-checking" role="status" aria-label="Checking your sign-in"><LoaderCircle size={14} className="spin" aria-hidden="true" /></span>;
  if (status === "signed-out") {
    return (
      <span className="mcp-acct account-signin-wrap">
        <button type="button" className={`btn btn-sm ${compact ? "btn-ghost" : "btn-outline"} account-signin`} onClick={() => openSignIn()} title="Only needed to plan together">Sign in</button>
      </span>
    );
  }
  const name = profile?.displayName ?? "Account";
  return (
    <div className="account-wrap mcp-acct" ref={wrap}>
      <button type="button" className={`avatar-btn${open ? " is-open" : ""}`} onClick={() => openAccountMenu(!open)} title={`Signed in as ${name}`} aria-haspopup="dialog" aria-expanded={open}>
        <span className="avatar" style={{ "--c": profile?.color } as CSSProperties}>{[...name][0]?.toUpperCase()}</span>
        {!compact && <span className="account-name">{name}</span>}
      </button>
      {open && <AccountMenu wrap={wrap} onClose={() => openAccountMenu(false)} />}
    </div>
  );
}

function AccountMenu({ onClose, wrap }: { onClose: () => void; wrap: React.RefObject<HTMLDivElement | null> }) {
  const user = useAccount((s) => s.user);
  const profile = useAccount((s) => s.profile);
  const hasPassword = useAccount((s) => s.hasPassword);
  const [name, setName] = useState(profile?.displayName ?? "");
  const [color, setColor] = useState(profile?.color ?? PROFILE_COLORS[0].c);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [actError, setActError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const dirty = !!profile && (name.trim() !== profile.displayName || color !== profile.color);

  // a new profile from the database (loaded after the menu opened): show it, unless it's being edited
  const seen = useRef(profile);
  useEffect(() => {
    if (profile && profile !== seen.current && !dirty) { setName(profile.displayName); setColor(profile.color); }
    seen.current = profile;
  }, [profile, dirty]);

  // outside click and Escape close it (not while the password dialog is open over it)
  useEffect(() => {
    if (pwOpen) return;
    const down = (e: PointerEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); (wrap.current?.querySelector(".avatar-btn") as HTMLElement | null)?.focus(); } };
    document.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key);
    return () => { document.removeEventListener("pointerdown", down, true); window.removeEventListener("keydown", key); };
  }, [onClose, wrap, pwOpen]);

  const run = async (what: string, f: () => Promise<void>, ok?: string) => {
    setBusy(what);
    if (what === "save") setMsg(null); else setActError(null);
    try { await f(); if (ok) setMsg({ ok: true, text: ok }); } catch (e) {
      const text = (e as Error).message;
      if (what === "save") setMsg({ ok: false, text }); else setActError(text);
    } finally { setBusy(null); }
  };
  const display = profile?.displayName ?? "Account";

  return (
    <div className="account-menu acct-menu" role="dialog" aria-label="Account">
      <div className="acct-id">
        <span className="avatar acct-avatar" style={{ "--c": profile?.color } as CSSProperties}>{[...display][0]?.toUpperCase()}</span>
        <span className="acct-who"><b>{display}</b><span className="caption">{user?.email ?? ""}</span></span>
        <button type="button" className="btn btn-sm btn-ghost btn-icon acct-close" onClick={onClose} title="Close (Esc)" aria-label="Close"><X size={16} aria-hidden="true" /></button>
      </div>

      <form className="acct-sec acct-profile" onSubmit={(e) => { e.preventDefault(); if (dirty) void run("save", () => updateProfile({ displayName: name, color }), "Saved."); }}>
        <div className="auth-field">
          <label htmlFor="acct-name" className="field-label">Name <span className="caption">shown to friends</span></label>
          <label className="field-box acct-box">
            <input id="acct-name" className="acct-name-input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="nickname" />
            <span className="caption tabular">{[...name].length}/40</span>
          </label>
        </div>
        <Swatches value={color} onChange={setColor} small />
        {(dirty || msg) && (
          <div className="acct-save-row">
            {msg && <span className={`caption ${msg.ok ? "ok-text" : "err-text"}`} role="status">{msg.text}</span>}
            {dirty && <button type="submit" className="btn btn-sm btn-primary acct-save" disabled={!!busy}>{busy === "save" ? <><Spin />Saving…</> : "Save"}</button>}
          </div>
        )}
      </form>

      <div className="acct-sec acct-row">
        <KeyRound size={16} aria-hidden="true" className="muted-ico" />
        <span className="acct-row-text"><span>Password</span><span className="caption">{hasPassword ? "Sign in with it or with a code" : "Sign in with emailed codes, or set one"}</span></span>
        <button type="button" className="btn btn-sm btn-outline acct-password" onClick={() => setPwOpen(true)}>{hasPassword ? "Change password" : "Set a password"}</button>
      </div>

      <div className="acct-sec"><BillingSection /></div>

      <div className="acct-sec acct-items">
        <button type="button" className="acct-item" disabled={!!busy} onClick={() => void run("signout", async () => { await signOut(); onClose(); })}>
          {busy === "signout" ? <Spin /> : <LogOut size={16} aria-hidden="true" />}Sign out
        </button>
        {!confirmDelete
          ? <button type="button" className="acct-item is-danger acct-delete" onClick={() => setConfirmDelete(true)}><Trash2 size={16} aria-hidden="true" />Delete account…</button>
          : (
            <div className="confirm-delete acct-confirm" role="alertdialog" aria-label="Delete your account?">
              <p>This deletes your Lab43 account for good, for MC Planner and the Minecraft mod, and ends any subscription (no more charges). Plans on this computer stay: they never needed an account.</p>
              <div className="acct-confirm-row">
                <button type="button" className="btn btn-sm btn-outline" autoFocus onClick={() => setConfirmDelete(false)}>Keep it</button>
                <button type="button" className="btn btn-sm btn-danger" disabled={!!busy} onClick={() => void run("delete", async () => { await deleteAccount(DEFAULT_SERVER); onClose(); })}>
                  {busy === "delete" ? <><Spin />Deleting…</> : "Delete account"}
                </button>
              </div>
            </div>
          )}
        {actError && <p className="field-error" role="alert"><CircleAlert size={13} aria-hidden="true" />{actError}</p>}
      </div>
      {pwOpen && <PasswordDialog onClose={() => setPwOpen(false)} />}
    </div>
  );
}

// ---------------- password (account menu) ----------------

/** Set or change the password. Supabase may ask for an emailed code first (*Secure password change*) or the current one. */
function PasswordDialog({ onClose }: { onClose: () => void }) {
  const ref = useModal(onClose);
  const hasPassword = useAccount((s) => s.hasPassword);
  const user = useAccount((s) => s.user);
  const [mode] = useState(hasPassword ? "change" : "set");
  const [password, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [current, setCurrent] = useState("");
  const [needCurrent, setNeedCurrent] = useState(false);
  const [code, setCode] = useState("");
  const [needCode, setNeedCode] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [saved, setSaved] = useState(false);
  const online = useOnline();

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const run = async (what: string, f: () => Promise<void>) => {
    if (busy) return;
    setBusy(what);
    setErrors({});
    try { await f(); } catch (e) { const p = authProblem(e, "password"); setErrors({ [p.field === "email" ? "form" : p.field]: p.message }); } finally { setBusy(null); }
  };
  const askCode = () => run("code", async () => { await sendReauthCode(); setNeedCode(true); setCode(""); setResendIn(RESEND_SECONDS); });
  const save = () => run("save", async () => {
    const p = checkNewPassword(password, again);
    if (p) throw p;
    if (needCode && code.length < CODE_LENGTH) throw new AuthProblem(`Enter the ${CODE_LENGTH}-digit code from the email.`, "code");
    if (needCurrent && !current) throw new AuthProblem("Enter your current password.", "current");
    const r = await setPassword(password, { nonce: needCode ? code : undefined, current: needCurrent ? current : undefined });
    if (r.saved) { setSaved(true); return; }
    if (r.need === "current") { setNeedCurrent(true); if (r.error) setErrors({ current: r.error }); return; }
    // an emailed code first (a sign-in older than a day)
    if (r.error) { setErrors({ code: r.error }); setCode(""); return; }
    await sendReauthCode().catch((e) => { const q = authProblem(e, "send"); setErrors({ form: q.message }); });
    setNeedCode(true);
    setCode("");
    setResendIn(RESEND_SECONDS);
  });

  return (
    <dialog ref={ref} className="acct-dialog mcp-acct password-dialog" aria-labelledby="pw-title">
      <div className="acct-dialog-head">
        <h2 className="title-sm" id="pw-title">{mode === "change" ? "Change password" : "Set a password"}</h2>
        <button type="button" className="btn btn-sm btn-ghost btn-icon" onClick={onClose} aria-label="Close" title="Close (Esc)"><X size={16} aria-hidden="true" /></button>
      </div>
      {saved
        ? (
          <>
            <p className="acct-dialog-lead"><Check size={15} className="accent" aria-hidden="true" /> Password saved. Sign in with <span className="auth-strong">{user?.email}</span> and this password next time, here, on auth.lab43.dev and in GameSweep.</p>
            <p className="caption">Other devices that are signed in to your Lab43 account are signed out within the hour.</p>
            <div className="acct-dialog-actions"><button type="button" className="btn btn-md btn-primary" autoFocus data-autofocus="" onClick={onClose}>Done</button></div>
          </>
        )
        : (
          <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
            <p className="acct-dialog-lead">One password for your Lab43 account. Emailed codes keep working too.</p>
            {!online && <p className="auth-offline" role="status"><WifiOff size={14} aria-hidden="true" />You're offline. Saving a password needs the internet.</p>}
            <input type="email" name="email" autoComplete="username" value={user?.email ?? ""} readOnly hidden />
            {needCurrent && <PasswordField id="pw-current" label="Current password" value={current} onChange={setCurrent} error={errors.current} autoComplete="current-password" autoFocus />}
            <PasswordField id="pw-new" label="New password" value={password} onChange={setPw} error={errors.password} autoComplete="new-password" autoFocus={!needCurrent} />
            <PasswordField id="pw-again" label="Repeat password" value={again} onChange={setAgain} error={errors.again} autoComplete="new-password" />
            <Rules password={password} again={again} />
            {needCode && (
              <div className="pw-code">
                <p className="caption">To keep your account safe, we emailed a code to <span className="auth-strong">{user?.email}</span>.</p>
                <CodeBoxes value={code} onChange={setCode} error={errors.code} label="Code from the email" />
                <div className="auth-code-row">
                  {resendIn > 0 ? <span className="tabular auth-wait">Send again in {mmss(resendIn)}</span>
                    : <button type="button" className="auth-link" disabled={!!busy} onClick={() => void askCode()}>Send a new code</button>}
                </div>
              </div>
            )}
            {errors.form && <p className="field-error" role="alert"><CircleAlert size={13} aria-hidden="true" />{errors.form}</p>}
            <div className="acct-dialog-actions">
              <button type="button" className="btn btn-md btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-md btn-primary pw-save" disabled={!!busy}>{busy === "save" ? <><Spin />Saving…</> : "Save password"}</button>
            </div>
          </form>
        )}
    </dialog>
  );
}
